import { useState } from 'react';

import { Radio } from 'antd';
import { useUpdateKycFlowStatusMutation } from 'api/IndividualsApiSlice';
import { BaseButton, StatusTag, useModal } from 'components';
import { useToast } from 'providers/ToastProvider';
import { GetPersonModel } from 'solstice-types';
import { getFullName } from 'utils';

interface StatusChangeModalProps {
  selectedIndividual: GetPersonModel;
  lastKycId: string;
}

export const StatusChangeModal = ({ selectedIndividual, lastKycId }: StatusChangeModalProps) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');

  const { firstName, lastName, status = '', kycLoginUrl } = selectedIndividual;
  const [updateKycStatus, { isLoading }] = useUpdateKycFlowStatusMutation();

  const { closeModal } = useModal();
  const toast = useToast();

  const onRadioChange = (e: any) => {
    setSelectedValue(e.target.value);
  };
  const onCancel = () => {
    closeModal();
  };

  const handleUpdateStatus = async () => {
    if (selectedValue) {
      try {
        await updateKycStatus({ id: lastKycId ?? '', status: selectedValue, note: note }).unwrap();
        closeModal();

        toast.success('Status updated successfully');
      } catch {
        toast.error('Oops! Something went wrong. Please try again.');
      }
    }
  };
  return (
    <div className="flex flex-col">
      <div className="text-gray-900 font-semibold text-lg mb-1.5">
        {' '}
        {getFullName(firstName, lastName) ?? 'N/A'}
      </div>
      <StatusTag status={status} />
      <span className="text-gray-600 my-[14px]">
        This individual was marked as a Potential Sanctions Match by the ID verification process.
        Please choose whether to mark it as passed or failed.
      </span>
      <Radio.Group
        onChange={onRadioChange}
        value={selectedValue}
        className="flex flex-col font-semibold gap-[14px]"
      >
        <Radio
          className="text-success-700 h-10 bg-gray-50 w-full flex items-center px-2.5"
          value={'ManuallyApproved'}
          checked={selectedValue === 'ManuallyApproved'}
        >
          Mark as Passed
        </Radio>
        <Radio
          checked={selectedValue === 'ManuallyRejected'}
          value={'ManuallyRejected'}
          className="text-error-600 h-10 bg-gray-50 w-full flex items-center px-2.5"
        >
          Mark as Failed
        </Radio>
      </Radio.Group>
      <span className="nold mt-2">Note*</span>
      <textarea
        className="w-full h-20 p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:border-blue-500"
        placeholder="Enter note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={200}
      />
      <span className="text-gray-500 text-xs">
        This note will be visible in the Audit Log for this individual.
      </span>
      <span className="mt-[14px]">
        You can see the full report by{' '}
        <a
          href={kycLoginUrl}
          target="_blank"
          className="font-medium text-blue-400"
          rel="noreferrer"
        >
          clicking here.
        </a>
      </span>

      <div className="gap-2.5 flex justify-between w-full pt-8">
        <BaseButton className="min-w-[112px] secondary-button" onClick={onCancel}>
          Cancel
        </BaseButton>

        <BaseButton
          loading={isLoading}
          className="primary-button text-white"
          onClick={handleUpdateStatus}
          disabled={!selectedValue || note === ''}
        >
          Confirm
        </BaseButton>
      </div>
    </div>
  );
};
